import { CreateWithBudButton } from "../../assistant/components/CreateWithBudButton";
import React, { useEffect, useState } from "react";
import { FruitifyContainer } from "../../../shared-components/Containers";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useApiClient } from "../../../api";
import dayjs from "dayjs";
import { withPageTracking } from "../../../services/postHog";
import { AddDetails } from "../../../shared-components/workflows/AddDetails";
import { AddQuestions } from "../../../shared-components/workflows/AddQuestions";
import { enqueueSnackbar } from "notistack";

const QuizCreateNew = ({ existingQuizGroup = null, onPageRefresh = null }) => {
  const [schoolClass, setSchoolClass] = useState(null);
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [quizDetails, setQuizDetails] = useState({});

  const ApiClient = useApiClient();
  const [searchParams] = useSearchParams();
  const classId = searchParams.get("classId");
  const navigate = useNavigate();

  const fetchClass = async () => {
    try {
      const response = await ApiClient.schools.getSchoolClassData({
        schoolClass: classId,
      });
      if (response) {
        setSchoolClass(response.schoolClass);
        setQuizDetails({
          id: "",
          name: "",
          description: "",
          schoolClass: response.schoolClass,
          subject: response.schoolClass.subject,
          educationLevel: response.schoolClass.educationLevel,
          yearGroup: response.schoolClass.yearGroup,
          dueDate: dayjs(),
        });
      }
    } catch (error) {
      console.error("Failed to fetch question", error);
    }
  };

  useEffect(() => {
    if (classId) {
      fetchClass();
    }
  }, [classId]);

  useEffect(() => {
    if (existingQuizGroup) {
      setSchoolClass(existingQuizGroup.schoolClass);
      setQuizDetails({
        id: existingQuizGroup.id,
        name: existingQuizGroup.name,
        description: existingQuizGroup.description,
        context: existingQuizGroup.context,
        schoolClass: existingQuizGroup.schoolClass,
        subject: existingQuizGroup.schoolClass.subject,
        educationLevel: existingQuizGroup.schoolClass.educationLevel,
        yearGroup: existingQuizGroup.schoolClass.yearGroup,
        dueDate: dayjs(existingQuizGroup.dueDate),
      });
      if (Array.isArray(existingQuizGroup?.quizzes)) {
        // Assuming your results contain QuizNode(s) with quizQuestions array
        // TODO: here - for now we are assuming just 1 quiz and we are flattening all questions into one
        //  In the future, we will have this layered as may have multiple quizzes (sub groups of a class)
        const questions = existingQuizGroup?.quizzes?.flatMap((r) =>
          (r.quizQuestions || []).map((qq) => qq.question),
        );
        setQuizQuestions(questions);
      }
    }
  }, [existingQuizGroup]);

  const handleQuizDetailsChange = (details) => {
    setQuizDetails(details);
    handleUpdateQuiz(quizQuestions, details);
  };

  const handleSchoolClassChange = (schoolClass) => {
    setSchoolClass(schoolClass);
    setQuizDetails((prev) => {
      if (!schoolClass) {
        return {
          ...prev,
          schoolClass: null,
          subject: null,
          educationLevel: null,
          yearGroup: null,
        };
      }

      return {
        ...prev,
        schoolClass: schoolClass,
        subject: schoolClass.subject,
        educationLevel: schoolClass.educationLevel,
        yearGroup: schoolClass.yearGroup,
      };
    });
  };

  const detailsComplete = Boolean(
    quizDetails.name?.trim() &&
      quizDetails.description?.trim() &&
      quizDetails.subject &&
      quizDetails.educationLevel &&
      quizDetails.yearGroup &&
      quizDetails.dueDate,
  );

  const handleRemoveQuestionsFromQuiz = async (question) => {
    try {
      const response = await ApiClient.quiz.removeQuestion({
        quiz: existingQuizGroup.quizzes[0].id,
        question: question.id,
      });
      if (response.success) {
        onPageRefresh();
      } else {
        enqueueSnackbar(`${response.message}`, {
          variant: "error",
          autoHideDuration: 3000,
        });
      }
    } catch (error) {
      console.log(error);
      enqueueSnackbar("Error removing question, please try again", {
        variant: "error",
        autoHideDuration: 3000,
      });
    }
  };

  const handleUpdateQuiz = async (questions, newDetails = null) => {
    try {
      if (!detailsComplete) {
        return;
      }
      const details = newDetails ?? quizDetails;
      const allQuestionIds = [
        ...questions.map((q) => q.id), // new questions
        ...(quizQuestions?.map((qq) => qq.id) || []), // existing quizQuestions
      ];
      // Remove duplicates
      const uniqueQuestionIds = Array.from(new Set(allQuestionIds));
      if (!uniqueQuestionIds?.length > 0) {
        return;
      }
      const response = await ApiClient.quiz.createOrUpdateQuizGroup({
        quizGroup: details.id,
        name: details.name,
        description: details.description,
        dueDate: dayjs(details.dueDate).format("YYYY-MM-DD"),
        schoolClass: details.schoolClass?.id,
        quizContext: details.context,
        questions: uniqueQuestionIds,
      });
      if (response.success) {
        if (details.id === response.quizGroup.id) {
          onPageRefresh();
        } else {
          navigate(`/quizzes/view/${response.quizGroup.id}`);
        }
      } else {
        enqueueSnackbar(`${response.message}`, {
          variant: "error",
          autoHideDuration: 3000,
        });
      }
    } catch (error) {
      console.log(error);
      enqueueSnackbar("Error submitting, please try again", {
        variant: "error",
        autoHideDuration: 3000,
      });
    }
  };

  const handlePublishQuiz = async () => {
    try {
      const response = await ApiClient.quiz.publishQuiz({
        quizGroup: quizDetails.id,
      });
      if (response.success) {
        onPageRefresh();
      } else {
        enqueueSnackbar(`${response.message}`, {
          variant: "error",
          autoHideDuration: 3000,
        });
      }
    } catch (error) {
      enqueueSnackbar("Error publishing quiz, please try again", {
        variant: "error",
        autoHideDuration: 3000,
      });
    }
  };

  return (
    <FruitifyContainer>
      <AddDetails
        renderAdditionalActions={(onOpened) => (
          <CreateWithBudButton
            kind="quiz"
            details={quizDetails}
            unsaved={true}
            onOpened={onOpened}
          />
        )}
        details={quizDetails}
        onChange={handleQuizDetailsChange}
        schoolClass={schoolClass}
        onSchoolClassChange={handleSchoolClassChange}
        canBePublished={detailsComplete && quizQuestions.length > 0}
        onComplete={handlePublishQuiz}
        quiz
      />
      {schoolClass && (
        <AddQuestions
          details={quizDetails}
          disabled={!detailsComplete}
          questions={quizQuestions}
          onAddQuestions={handleUpdateQuiz}
          onRemoveQuestions={handleRemoveQuestionsFromQuiz}
          ongoingJobs={existingQuizGroup?.ongoingJobs || []}
          onRefresh={onPageRefresh}
          quiz
        />
      )}
    </FruitifyContainer>
  );
};

export default withPageTracking(QuizCreateNew, {
  feature: "quiz-create-new",
});
